import { createContext, useContext, useEffect, useState } from "react";
import {
  AUTH_TOKEN_KEY,
  AUTH_CHANGED_EVENT,
  SESSION_EXPIRED_EVENT,
  expireSession,
  getSessionUser,
  isAdminSession,
} from "../utils/authSession";

const AuthContext = createContext({ isAuthenticated: false, isAdmin: false, user: null, sessionExpired: false });

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(AUTH_TOKEN_KEY));
  const [sessionExpired, setSessionExpired] = useState(() => isAdminSession(token) && !getSessionUser(token));
  const user = getSessionUser(token);

  useEffect(() => {
    let timer;
    const syncSession = () => {
      clearTimeout(timer);
      const currentToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const currentUser = getSessionUser(currentToken);
      if (currentToken && !currentUser) {
        expireSession(currentToken);
        return;
      }
      setToken(currentToken);
      if (currentUser) {
        setSessionExpired(false);
        timer = setTimeout(syncSession, Math.min(currentUser.exp * 1000 - Date.now(), 2147483647));
      }
    };
    const onStorage = (event) => {
      if (event.key === AUTH_TOKEN_KEY || event.key === null) syncSession();
    };
    const onSessionExpired = () => setSessionExpired(true);

    window.addEventListener(AUTH_CHANGED_EVENT, syncSession);
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", syncSession);
    document.addEventListener("visibilitychange", syncSession);
    syncSession();
    return () => {
      clearTimeout(timer);
      window.removeEventListener(AUTH_CHANGED_EVENT, syncSession);
      window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", syncSession);
      document.removeEventListener("visibilitychange", syncSession);
    };
  }, [token]);

  return (
    <AuthContext.Provider value={{ isAuthenticated: !!user, isAdmin: user?.isAdmin === true, user, sessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
